import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '@/hooks/useAnalytics';

const ArrowRightIcon = ({ size = 16 }: { size?: number }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14" />
    <path d="m12 5 7 7-7 7" />
  </svg>
);

const CheckIcon = ({ size = 14, className = '' }: { size?: number; className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

export const FinalCTASection = React.memo(() => {
  const { t } = useTranslation();
  const { track } = useAnalytics();

  return (
    <section className="py-24 px-6 bg-white relative overflow-hidden">
      <div className="max-w-5xl mx-auto relative">
        <div className="relative rounded-3xl bg-black text-white px-8 md:px-16 py-16 md:py-20 overflow-hidden shadow-2xl shadow-black/20">
          <div
            className="absolute -top-32 -right-32 w-96 h-96 rounded-full pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)' }}
            aria-hidden="true"
          />
          <div
            className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full pointer-events-none"
            style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)' }}
            aria-hidden="true"
          />

          <div className="relative z-10 flex flex-col items-center text-center">
            <span className="inline-flex items-center gap-2 text-xs font-medium px-3 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-gray-700 animate-pulse" />
              {t('landing.finalCta.badge')}
            </span>

            <h2
              className="text-3xl md:text-5xl lg:text-6xl font-medium leading-tight mb-5 max-w-3xl"
              style={{
                background: 'linear-gradient(to bottom, #ffffff, #ffffff, rgba(255, 255, 255, 0.7))',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                letterSpacing: '-0.04em',
              }}
            >
              {t('landing.finalCta.titleLine1')}
              <br />
              {t('landing.finalCta.titleLine2')}
            </h2>

            <p className="text-sm md:text-base max-w-xl mb-8 text-white/70">{t('landing.finalCta.subtitle')}</p>

            <div className="flex flex-col sm:flex-row items-center gap-3 mb-6">
              <Link
                to="/auth?tab=signup"
                onClick={() => track('landing_cta_clicked', { location: 'final_cta' })}
                className="inline-flex items-center justify-center gap-2 h-12 px-8 text-base font-medium rounded-lg bg-white text-black hover:scale-105 active:scale-95 transition-all shadow-lg"
              >
                {t('landing.finalCta.ctaPrimary')}
                <ArrowRightIcon size={16} />
              </Link>
              <a
                href="#"
                className="inline-flex items-center justify-center gap-2 h-12 px-8 text-base font-medium rounded-lg border border-white/20 text-white hover:bg-white/10 transition-all"
              >
                {t('landing.finalCta.ctaSecondary')}
              </a>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-white/60">
              <span className="flex items-center gap-1.5"><CheckIcon size={14} className="text-gray-700" /> {t('landing.finalCta.checklistFree')}</span>
              <span className="flex items-center gap-1.5"><CheckIcon size={14} className="text-gray-700" /> {t('landing.finalCta.checklistNoCard')}</span>
              <span className="flex items-center gap-1.5"><CheckIcon size={14} className="text-gray-700" /> {t('landing.finalCta.checklistCancel')}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
});
FinalCTASection.displayName = 'FinalCTASection';
